import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Drawer } from '@material-ui/core';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import CloseIcon from '@material-ui/icons/Close';
import {
  AppButton,
  AppFormErrorText,
  AppIcon,
  AppIconButton,
  AppModal,
  MotifAppInput,
  MotifAppSelect,
  MotifAppTextArea,
} from '../../theme';
import { add, xMark } from '../../theme/icons';
import t from '../../localization/en/translation.json';
import {
  createAnnouncementRequest,
  deleteAnnouncementRequest,
  flushAnnouncements,
  getAnnouncementsRequest,
  readAnnouncementRequest,
  updateAnnouncementRequest,
  updateReadAnnouncement,
} from '../../store/actions/project-actions';
import {
  ANNOUNCEMENT_SCROLL_TARGET,
  announcementDropdownOptions,
  isImportantDropDown,
} from '../../containers/projectDetails/projectDetailConstants';
import './projectAnnouncements.scss';
import { validateFormInputForSpecialChar } from '../../utils/helper-utility';
import { IUser } from '../../models/user';
import { AnnouncementTabPanel } from './announcementTabPanel/announcementTabPanel';

interface Announcements {
  isImportant: boolean;
  name: string;
  details: string;
  createdByUserName: string;
  authorId: string;
  modifiedDateTime: string;
  id: string;
  status: string;
  makeAnnouncementRead?: Function;
}

interface RootState {
  projects: {
    announcements: Announcements[];
    announcementPaginationData: AnnouncementPaginationData;
    deletedAnnouncement: {
      announcementId: string;
    };
    savedAnnouncement: object;
    showReadAnnouncementFailure?: boolean;
  };
}

interface AnnouncementPaginationData {
  chunk: number;
  isAllAnnouncementsFetched: boolean;
  isLoading: boolean;
}

interface ProjectAnnouncementsProps {
  flyout: boolean;
  closeFlyout: () => void;
  isProjectAdmin: boolean;
  projectId: string;
  isAnnouncementOpen: boolean;
  currentUser: IUser;
}

const selectAnnouncements = (state: RootState) => state.projects.announcements;
const selectAnnouncementPaginationData = (state: RootState) =>
  state.projects.announcementPaginationData;
const selectDeletedAnnouncement = (state: RootState) =>
  state.projects.deletedAnnouncement;
const selectSavedAnnouncement = (state: RootState) =>
  state.projects.savedAnnouncement;

const ProjectAnnouncements: React.FC<ProjectAnnouncementsProps> = ({
  flyout,
  closeFlyout,
  isProjectAdmin,
  projectId,
  isAnnouncementOpen = false,
  currentUser,
}) => {
  const dispatch = useDispatch();
  const announcements = useSelector(selectAnnouncements);
  const deletedAnnouncement = useSelector(selectDeletedAnnouncement);
  const savedAnnouncement = useSelector(selectSavedAnnouncement);
  const {
    isLoading = true,
  } = useSelector(selectAnnouncementPaginationData);

  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [showDeleteAnnouncementModal, setShowDeleteAnnouncementModal] =
    useState(false);
  const [announcementDetails, setAnnouncementDetails] = useState('');
  const [announcementName, setAnnouncementName] = useState('');
  const [isImportantAnnouncement, setIsImportantAnnouncement] = useState(
    isImportantDropDown.NO
  );
  const [announcementId, setAnnouncementId] = useState('');
  const [value, setValue] = React.useState(0);

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    getAnnouncementChunk();
    setValue(newValue);
  };

  const handleAnnouncementModalClose = () => {
    setShowAnnouncementModal(false);
    const timeoutId = setTimeout(() => {
      setAnnouncementDetails('');
      setAnnouncementName('');
      setIsImportantAnnouncement(isImportantDropDown.NO);
      setAnnouncementId('');
    }, 60);

    return () => clearTimeout(timeoutId);
  };

  const createAnnouncementModel = (showAnnouncementModal: boolean) => {
    setShowAnnouncementModal(showAnnouncementModal);
  };

  const handleAnnouncementSave = () => {
    const isImportant = isImportantAnnouncement === isImportantDropDown.YES;
    const announcementData = {
      projectId,
      name: announcementName,
      details: announcementDetails,
      isImportant,
      status: 'UnRead',
    };
    if (announcementId) {
      dispatch(
        updateAnnouncementRequest({ ...announcementData, id: announcementId })
      );
    } else {
      dispatch(createAnnouncementRequest(announcementData));
    }
    handleAnnouncementModalClose();
  };

  const handleDeleteAnnouncement = () => {
    if (announcementId) {
      dispatch(deleteAnnouncementRequest({ projectId, announcementId }));
    }
    toggleDeleteAnnouncementModal(false);
  };

  const toggleDeleteAnnouncementModal = (
    showDeleteAnnouncementModal: boolean,
    announcementId?: string
  ) => {
    setShowDeleteAnnouncementModal(showDeleteAnnouncementModal);
    if (showDeleteAnnouncementModal && announcementId) {
      setAnnouncementId(announcementId);
    } else {
      setAnnouncementId('');
    }
  };

  const toggleEditAnnouncementModal = (
    showAnnouncementModal: boolean,
    announcementId?: string
  ) => {
    const announcement = announcements.find(
      (announcement) => announcement.id === announcementId
    );
    setShowAnnouncementModal(showAnnouncementModal);
    if (showAnnouncementModal && announcementId && announcement) {
      const isImportantAnnouncement = announcement.isImportant
        ? isImportantDropDown.YES
        : isImportantDropDown.NO;
      setAnnouncementId(announcement.id);
      setAnnouncementName(announcement.name);
      setAnnouncementDetails(announcement.details);
      setIsImportantAnnouncement(isImportantAnnouncement);
    } else {
      setAnnouncementId('');
    }
  };

  const getAnnouncementChunk = useCallback(() => {
    const userId = currentUser?.id;
    dispatch(getAnnouncementsRequest({ projectId, pageNumber: 1, userId }));
  }, [dispatch, projectId, currentUser]);

  useEffect(() => {
    if (deletedAnnouncement && deletedAnnouncement.announcementId) {
      dispatch(flushAnnouncements());
      getAnnouncementChunk();
    }
  }, [deletedAnnouncement, dispatch, getAnnouncementChunk]);

  useEffect(() => {
    if (savedAnnouncement && Object.keys(savedAnnouncement).length) {
      dispatch(flushAnnouncements());
      getAnnouncementChunk();
    }
  }, [savedAnnouncement, dispatch, getAnnouncementChunk]);

  useEffect(() => {
    if (!flyout) getAnnouncementChunk();
  }, [projectId, getAnnouncementChunk, flyout]);

  const handleAnnouncementDetailsChange = (e) => {
    setAnnouncementDetails(e.target.value);
  };

  const handleAnnouncementNameChange = (e) => {
    setAnnouncementName(e.target.value);
  };

  const handleIsImportantAnnouncementChange = (e) => {
    setIsImportantAnnouncement(e.target.value);
  };

  const makeAnnouncementRead = (announcementId) => {
    if (announcementId) {
      handleAnnouncementRead(announcementId);
      const userId = currentUser?.id;
      const data = {
        id: announcementId,
        status: 'Read',
      };
      dispatch(readAnnouncementRequest({ projectId, userId, data }));
    }
  };
  const handleAnnouncementRead = (announcementId: string) => {
    const updatedAnnounce = announcements.map((announcement) => {
      if (announcement.id === announcementId) {
        return { ...announcement, status: 'Read' };
      }
      return announcement;
    });
    dispatch(updateReadAnnouncement(updatedAnnounce));
  };

  function TabPanel(props: any) {
    const { children, value, index, ...other } = props;

    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {value === index && <p>{children}</p>}
      </div>
    );
  }

  return (
    <>
      <Drawer
        className="project-announcements"
        anchor="right"
        open={flyout}
        onClose={closeFlyout}
        classes={{ paper: 'drawer-paper' }}
        ModalProps={{ keepMounted: true }}
      >
        <div className="drawer-content">
          <div className="announcement-header m-b-15">
            <p className="announcement-label">{t.edit_project_announcement}</p>
            <div className="create-announcement">
              <div className="create-announcement-btn">
                <AppButton
                  variant={'outlined'}
                  onClick={() => createAnnouncementModel(true)}
                >
                  <AppIcon icon={add} className="m-r-10" />{' '}
                  {t.announcement_create_btn_text}
                </AppButton>
              </div>
              <span className="close-button">
                <AppIcon className="icon" onClick={closeFlyout} icon={xMark} />
              </span>
            </div>
          </div>
          <div className="announce-tab-wrapper">
            <Tabs value={value} onChange={handleTabChange}>
              <Tab label={t.announcement_tab1} />
              <Tab label={t.announcement_tab2} />
              <Tab label={t.announcement_tab3} />
            </Tabs>
            <TabPanel value={value} index={0}>
              <div
                id={ANNOUNCEMENT_SCROLL_TARGET}
                className="announcement-list"
              >
                {!isLoading && !announcements.length ? (
                  <div className="no-announcements">
                  <h4>{t.no_announcement_yet}</h4>
                  <p>{t.stay_tuned_updates_appear}</p>
                </div>
                ) : (
                  <AnnouncementTabPanel
                    announcements={announcements}
                    isProjectAdmin={isProjectAdmin}
                    deleteAnnouncement={toggleDeleteAnnouncementModal}
                    editAnnouncement={toggleEditAnnouncementModal}
                    makeAnnouncementRead={makeAnnouncementRead}
                  />
                )}
              </div>
            </TabPanel>
            <TabPanel value={value} index={1}>
              <div
                id={ANNOUNCEMENT_SCROLL_TARGET}
                className="announcement-list"
              >
                {!isLoading && !announcements?.filter((v)=> v.status === 'UnRead')?.length ? (
                  <div className="no-announcements">
                  <h4>{t.no_unread_announcements}</h4>
                  <p>{t.no_unread_announcement_des}</p>
                </div>
                ) : (
                  <AnnouncementTabPanel
                    announcements={announcements?.filter((v)=> v.status === 'UnRead')}
                    isProjectAdmin={isProjectAdmin}
                    deleteAnnouncement={toggleDeleteAnnouncementModal}
                    editAnnouncement={toggleEditAnnouncementModal}
                    makeAnnouncementRead={makeAnnouncementRead}
                  />
                )}
              </div>
            </TabPanel>
            <TabPanel value={value} index={2}>
              <div
                id={ANNOUNCEMENT_SCROLL_TARGET}
                className="announcement-list"
              >
                {!isLoading && !announcements?.filter((v)=> v.isImportant)?.length ? (
                  <div className="no-announcements">
                  <h4>{t.no_critical_announcements}</h4>
                  <p>{t.no_critical_announcement_des}</p>
                </div>
                ) : (
                  <AnnouncementTabPanel
                    announcements={announcements?.filter((v)=> v.isImportant)}
                    isProjectAdmin={isProjectAdmin}
                    deleteAnnouncement={toggleDeleteAnnouncementModal}
                    editAnnouncement={toggleEditAnnouncementModal}
                    makeAnnouncementRead={makeAnnouncementRead}
                  />
                )}
              </div>
            </TabPanel>
          </div>
        </div>
      </Drawer>
      <div className="delete-announcement-modal">
        <AppModal
          size="sm"
          showModal={showDeleteAnnouncementModal}
          cancelBtnText={t.cancel}
          confirmBtnText={t.delete_draft_modal_delete_btn}
          onConfirm={() => handleDeleteAnnouncement()}
          onModalClose={() => toggleDeleteAnnouncementModal(false)}
          showCloseModelIcon={false}
          showDividers={false}
          modalClass={'delete-ann-model'}
        >
          <div className="delete-model-content">
            <p>{t.delete_announcement_modal_msg}</p>
            <span className="delete-close-model">
              <AppIcon
                className="icon"
                onClick={() => toggleDeleteAnnouncementModal(false)}
                icon={xMark}
              />
            </span>
          </div>
        </AppModal>
      </div>
      <div className="announcement-modal">
        <AppModal
          newDesign={true}
          size="md"
          modalClass="create-update-ann-modal"
          title={
            announcementId
              ? t.announcement_modal_edit_title
              : t.announcement_modal_title
          }
          showModal={showAnnouncementModal}
          cancelBtnText={t.cancel}
          confirmBtnText={
            announcementId ? t.update : t.announcement_save_btn_text
          }
          onConfirm={handleAnnouncementSave}
          onModalClose={handleAnnouncementModalClose}
          disabledValue={
            !announcementName.trim().length ||
            validateFormInputForSpecialChar(announcementName) ||
            validateFormInputForSpecialChar(announcementDetails)
          }
        >
          <div className="announcement-form">
            <div className="input-row">
              <div className="input-field">
                <span className="label">{t.announcement_name_label}</span>
                <MotifAppInput
                  value={announcementName}
                  maxLength={50}
                  isValid={!validateFormInputForSpecialChar(announcementName)}
                  onChange={handleAnnouncementNameChange}
                  placeholder={t.enterValue}
                />
                {!validateFormInputForSpecialChar(announcementName) || (
                  <AppFormErrorText>
                    {t.all_ssp_validate_special_character_in_text_error.replace(
                      '{1}',
                      announcementName.trim().split('')[0]
                    )}
                  </AppFormErrorText>
                )}
              </div>
              <div className="input-field">
                <span className="label">{t.announcement_important_label}</span>
                <MotifAppSelect
                  value={isImportantAnnouncement}
                  options={announcementDropdownOptions}
                  onChange={handleIsImportantAnnouncementChange}
                  placeholder="Select"
                />
              </div>
            </div>
            <div className="input-row">
              <div className="text-area-field">
                <span className="label">{t.announcement_details_label}</span>
                <div className="text-area-wrapper">
                  <MotifAppTextArea
                    maxLength={250}
                    value={announcementDetails}
                    isValid={
                      !validateFormInputForSpecialChar(announcementDetails)
                    }
                    onChange={handleAnnouncementDetailsChange}
                    placeholder={t.enterDescription}
                  />
                  {!validateFormInputForSpecialChar(announcementDetails) || (
                    <AppFormErrorText>
                      {t.all_ssp_validate_special_character_in_text_error.replace(
                        '{1}',
                        announcementDetails.trim().split('')[0]
                      )}
                    </AppFormErrorText>
                  )}
                  {announcementDetails.trim()?.length > 0 && (
                    <AppIconButton
                      aria-label="close"
                      className="text-area-close-icon"
                      onClick={() => setAnnouncementDetails('')}
                    >
                      <CloseIcon />
                    </AppIconButton>
                  )}
                </div>
              </div>
            </div>
          </div>
        </AppModal>
      </div>
    </>
  );
};

export default ProjectAnnouncements;
